import {ReactElement} from 'react';
import {I18n} from 'aws-amplify/utils';
import {Button} from '@mantine/core';
import {SimplePageHeader} from '@flexinet/ui-components';

import {ProviderConnection} from '../types';
import {revalidateLabel} from '../statusPresentation';

// FNP-1305 — THE DETAIL-PAGE HEADER, AND THE TOOLBAR CONTRACT.
//
// The module compliance checklist requires a detail-page header carrying the
// singular title and the record's identifying field, with `tools` as an ARRAY of
// elements each holding a stable `key` — not a hand-built `<Group>` of buttons.
// This page had no header at all, which is the structural reason its actions
// were stacked in a body card in the middle of the page and why FNP-1304 had
// nowhere to put a Rename.
//
// ⚠️ THE TITLE IS THE CONNECTION'S NAME, not "Provider Connection". The
// checklist asks for the record's identifying field, and `displayName` is the
// only human-readable handle a connection has — it is what the zones list and
// the certificate request flow name it by.
//
// ⚠️ ORDERED LEAST DESTRUCTIVE FIRST: rename changes a label, re-validate
// changes a credential, disconnect destroys a secret. An operator scanning for
// the cosmetic fix should not have to read past the irreversible one.
//
// ⚠️ THE HANDLERS LIVE IN `Profile`, NOT HERE. The drawers and the disconnect
// modal need the connection's state and the post-disconnect navigation, so this
// component takes callbacks rather than owning the state. That keeps the header
// a presentation component and leaves one place — `Profile` — where the action
// state lives.

export const DetailPageHeader = (
    {
        connection,
        onRename,
        onRevalidate,
        onDisconnect,
    }: {
        connection: ProviderConnection,
        onRename: () => void,
        onRevalidate: () => void,
        onDisconnect: () => void,
    },
) => {

    const tools: ReactElement[] = [
        <Button
            key={'rename'}
            variant={'outline'}
            onClick={onRename}
        >
            {I18n.get('Rename')}
        </Button>,
        <Button
            key={'revalidate'}
            variant={'outline'}
            onClick={onRevalidate}
        >
            {/* UNREACHABLE says "Retry"; INVALID says "Re-validate", because
                clicking again unchanged cannot fix a rejection. */}
            {revalidateLabel(connection.status)}
        </Button>,
        <Button
            key={'disconnect'}
            color={'red'}
            variant={'outline'}
            onClick={onDisconnect}
        >
            {I18n.get('Disconnect')}
        </Button>,
    ];

    return (
        <SimplePageHeader
            title={connection.displayName}
            tools={tools}
        />
    );
};
